import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import cn from 'classnames';

import { useMoments } from './MomentsContext';
import JudgeBadge from './JudgeBadge';
import StatusChip from './StatusChip';
import MomentsFeatureFlag from './MomentsFeatureFlag';
import MomentsPlans from './MomentsPlans';
import { formatUsd, momentPublicUrl, shortDate } from './moment_stream_helpers';

import './MomentsGallery.scss';
import './MomentsGalleryTable.scss';

const TABS = [
  { value: 'shops', display: 'Moments' },
  { value: 'plans', display: 'Plans' },
  { value: 'flags', display: 'Feature flag' }
];

// Whether the owner has opened the shop since its latest build; a dash until one has completed.
const SeenCell = ({ seen }) => {
  if (seen == null) return <span className='msa-table-empty'>—</span>;
  return <span className={cn('msa-table-seen', { 'is-unseen': !seen })}>{seen ? 'Seen' : 'Unseen'}</span>;
};

// "Harry Rein · Jul 21" cell for created / edited / published attribution.
const Attribution = ({ by, at }) => {
  if (!by && !at) return <span className='msa-table-empty'>—</span>;
  return (
    <span className='msa-table-attribution'>
      {by && by.name ? by.name : by ? `User ${by.id}` : null}
      {by && at ? ' · ' : ''}
      {shortDate(at)}
    </span>
  );
};

const GalleryTable = ({ gallery, onOpenMoment, onSetFeatured, featuringId }) => (
  <div className='msa-gallery-table-wrap'>
    <table className='msa-gallery-table'>
      <thead>
        <tr>
          <th>Title</th>
          <th>Featured</th>
          <th>Status</th>
          <th>Seen</th>
          <th>Judge</th>
          <th className='msa-th-num'>Products</th>
          <th className='msa-th-num'>Builds</th>
          <th className='msa-th-num'>Build cost</th>
          <th>Created</th>
          <th>Last edited</th>
          <th>Published</th>
        </tr>
      </thead>
      <tbody>
        {gallery.map(entry => (
          <tr key={entry.threadRef} onClick={() => onOpenMoment(entry)}>
            <td className='msa-td-title'>
              {entry.title}
              {entry.status === 'published' && entry.slug && (
                <a
                  className='msa-table-slug'
                  href={momentPublicUrl(entry.slug)}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={e => e.stopPropagation()}
                >
                  /moments/{entry.slug}
                </a>
              )}
            </td>
            {/* Feature/unfeature straight from the table — the row click
                (open moment) must not fire with it. Live shops only; a
                featured shop stays uncheckable-off in any status. */}
            <td className='msa-td-featured' onClick={e => e.stopPropagation()}>
              <input
                type='checkbox'
                title={
                  entry.status === 'published' || entry.featured
                    ? 'Featured Moments join the shopper discover page (/shop/discover/moments)'
                    : 'Only live (published) Moments can be featured'
                }
                checked={!!entry.featured}
                disabled={featuringId != null || (!entry.featured && entry.status !== 'published')}
                onChange={event => onSetFeatured(entry.id, event.target.checked)}
              />
            </td>
            <td>
              <StatusChip status={entry.status} runStatus={entry.runStatus} />
            </td>
            <td>
              <SeenCell seen={entry.seen} />
            </td>
            <td>
              <JudgeBadge verdict={entry.judgeVerdict} score={entry.judgeScore} />
            </td>
            <td className='msa-td-num'>{entry.productCount || 0}</td>
            <td className='msa-td-num'>{entry.buildCount || 0}</td>
            <td className='msa-td-num'>{entry.buildCostUsd ? formatUsd(entry.buildCostUsd) : '—'}</td>
            <td>
              <Attribution by={entry.createdBy} at={entry.createdAt} />
            </td>
            <td>
              <Attribution by={entry.lastEditedBy} at={entry.lastEditedAt} />
            </td>
            <td>
              <Attribution by={entry.publishedBy} at={entry.publishedAt} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Gallery landing: the operational table. Creation lives on the shopper
// surface (threads → brief → build) — admins observe and support, so there
// is no card browse or New-moment door here.
const MomentsGallery = () => {
  const { gallery, galleryError: loadError, openMoment: onOpenMoment, refreshGallery: onRetry, setFeaturedFor, featuringId } = useMoments();
  const history = useHistory();
  const location = useLocation();
  const activeTab = TABS.find(tab => tab.value === new URLSearchParams(location.search).get('tab'))?.value || 'shops';
  const setActiveTab = tab => {
    const next = new URLSearchParams(location.search);
    if (tab === 'shops') next.delete('tab');
    else next.set('tab', tab);
    history.push({ search: next.toString() });
  };

  return (
    <div className='msa-gallery-outer'>
      <div className='msa-gallery-header'>
        <div>
          <h1>Moments</h1>
          <div className='msa-subheader'>Moments built by shoppers with their personal shopper — observed and supported from here.</div>
        </div>
      </div>

      <div className='msa-gallery-tabs'>
        {TABS.map(tab => (
          <button
            key={tab.value}
            type='button'
            className={cn('msa-gallery-tab', { 'is-active': activeTab === tab.value })}
            onClick={() => setActiveTab(tab.value)}
          >
            {tab.display}
          </button>
        ))}
      </div>

      {activeTab === 'plans' && <MomentsPlans />}

      {activeTab === 'flags' && <MomentsFeatureFlag />}

      {activeTab === 'shops' && (
        <>
          {gallery === null && !loadError && (
            <div className='msa-gallery-loading'>
              <div className='msa-spinner' />
              <span>Loading Moments…</span>
            </div>
          )}

          {loadError && (
            <div className='msa-gallery-error'>
              <p>{loadError}</p>
              <button className='msa-primary-btn' onClick={onRetry}>
                Try again
              </button>
            </div>
          )}

          {gallery !== null && !loadError && gallery.length === 0 && (
            <div className='msa-gallery-empty'>
              <h2>No Moments yet</h2>
              <p>Moments appear here as shoppers build them.</p>
            </div>
          )}

          {gallery !== null && gallery.length > 0 && (
            <GalleryTable gallery={gallery} onOpenMoment={onOpenMoment} onSetFeatured={setFeaturedFor} featuringId={featuringId} />
          )}
        </>
      )}
    </div>
  );
};

export default MomentsGallery;
